import {
  LOADER_VERSION,
  createBridgeMessage,
  mergeHostContextPatch,
  parseMessageEvent,
  parseNovaToHostMessage,
  type HostApp,
  type HostToNovaMessage,
  type HostVisibility,
  type NovaHostContext,
  type NovaHostContextPatch,
  type NovaStateMessage,
} from './protocol';

const IFRAME_LOAD_TIMEOUT_MS = 30_000;
const HANDSHAKE_TIMEOUT_MS = 15_000;

export interface NovaHostBridgeTelemetry {
  name: string;
  data?: Record<string, unknown>;
}

export type NovaHostBridgeFatalReason = 'handshake-timeout' | 'incompatible-embed' | 'nova-error';

export interface NovaHostBridgeOptions {
  hostApp: HostApp;
  initialContext?: Omit<NovaHostContext, 'hostApp'>;
  requiredCapabilities?: string[];
  onState(message: NovaStateMessage['payload']): void;
  onLauncherMove?(movement: { deltaX: number; persist: boolean }): void;
  onFatalError(reason: NovaHostBridgeFatalReason): void;
  onTelemetry?(event: NovaHostBridgeTelemetry): void;
}

export class NovaHostBridge {
  private readonly options: NovaHostBridgeOptions;
  private iframe: HTMLIFrameElement | null = null;
  private context: NovaHostContext;
  private visibility: HostVisibility = { visible: true, focused: true };
  private open = false;
  private handshakeComplete = false;
  private loadTimeout: ReturnType<typeof setTimeout> | undefined;
  private handshakeTimeout: ReturnType<typeof setTimeout> | undefined;
  private failed = false;
  private loadCount = 0;
  private loggedMalformedTypes = new Set<string>();
  private onLauncherMove: NovaHostBridgeOptions['onLauncherMove'];

  constructor(options: NovaHostBridgeOptions) {
    this.options = options;
    this.context = { hostApp: options.hostApp, ...options.initialContext };
    this.onLauncherMove = options.onLauncherMove;
  }

  start(): void {
    window.addEventListener('message', this.handleMessage);
  }

  stop(): void {
    window.removeEventListener('message', this.handleMessage);
    this.clearTimeouts();
  }

  attachIframe(iframe: HTMLIFrameElement | null): void {
    this.iframe = iframe;
    this.handshakeComplete = false;
    this.loadCount = 0;
    this.clearTimeouts();
    if (!iframe) return;
    this.loadTimeout = setTimeout(() => this.failHandshake('iframe-load-timeout'), IFRAME_LOAD_TIMEOUT_MS);
  }

  notifyIframeLoaded(): void {
    if (this.failed) return;
    this.loadCount += 1;
    // A very fast initial document can post ready before the parent observes
    // its load event. That handshake is already complete and must not be reset.
    if (this.loadCount === 1 && this.handshakeComplete) return;
    this.handshakeComplete = false;
    if (this.loadTimeout) clearTimeout(this.loadTimeout);
    this.loadTimeout = undefined;
    if (this.handshakeTimeout) clearTimeout(this.handshakeTimeout);
    this.handshakeTimeout = setTimeout(() => this.failHandshake('ready-timeout'), HANDSHAKE_TIMEOUT_MS);
  }

  setContextPatch(patch: NovaHostContextPatch): void {
    this.context = mergeHostContextPatch(this.context, patch);
    if (this.handshakeComplete) this.post(createBridgeMessage('host:context', { patch }));
  }

  getContext(): NovaHostContext {
    return { ...this.context };
  }

  setVisibility(visibility: HostVisibility): void {
    this.visibility = visibility;
    if (this.handshakeComplete) this.post(createBridgeMessage('host:visibility', visibility));
  }

  sendOpen(): void {
    this.open = true;
    if (this.handshakeComplete) this.post(createBridgeMessage('host:open', {}));
  }

  sendClose(): void {
    this.open = false;
    if (this.handshakeComplete) this.post(createBridgeMessage('host:close', {}));
  }

  sendToggle(): void {
    this.open = !this.open;
    if (this.handshakeComplete) this.post(createBridgeMessage('host:toggle', {}));
  }

  isHandshakeComplete(): boolean {
    return this.handshakeComplete;
  }

  setLauncherMoveHandler(handler: NovaHostBridgeOptions['onLauncherMove']): void {
    this.onLauncherMove = handler;
  }

  private handleMessage = (event: MessageEvent): void => {
    if (event.origin !== window.location.origin || event.source !== this.iframe?.contentWindow) return;
    const parsed = parseMessageEvent({
      event,
      expectedOrigin: window.location.origin,
      expectedSource: this.iframe?.contentWindow ?? null,
      parse: parseNovaToHostMessage,
    });
    if (parsed.status === 'unknown') return;
    if (parsed.status === 'invalid') {
      const type = parsed.type ?? 'envelope';
      if (!this.loggedMalformedTypes.has(type)) {
        this.loggedMalformedTypes.add(type);
        console.warn(`[Nova embed] Dropped malformed ${type} message`);
        this.options.onTelemetry?.({ name: 'malformed_message', data: { type } });
      }
      return;
    }
    if (this.failed) return;

    const message = parsed.message;
    if (message.type === 'nova:ready') {
      const missingCapabilities = (this.options.requiredCapabilities ?? []).filter(
        (capability) => !message.payload.capabilities.includes(capability),
      );
      if (missingCapabilities.length > 0) {
        this.failed = true;
        this.clearTimeouts();
        this.options.onTelemetry?.({
          name: 'incompatible_embed',
          data: { missingCapabilities, embedVersion: message.payload.embedVersion, loaderVersion: LOADER_VERSION },
        });
        this.options.onFatalError('incompatible-embed');
        return;
      }
      this.handshakeComplete = true;
      this.failed = false;
      this.clearTimeouts();
      this.post(
        createBridgeMessage('host:init', {
          loaderVersion: LOADER_VERSION,
          hostApp: this.options.hostApp,
          context: this.context,
          visibility: this.visibility,
          open: this.open,
        }),
      );
      this.options.onTelemetry?.({
        name: 'handshake_ok',
        data: { embedVersion: message.payload.embedVersion, loaderVersion: LOADER_VERSION },
      });
      return;
    }
    if (message.type === 'nova:state') {
      if (!this.handshakeComplete) return;
      this.open = message.payload.open;
      this.options.onState(message.payload);
      return;
    }
    if (message.type === 'nova:launcher-move') {
      if (!this.handshakeComplete) return;
      this.onLauncherMove?.(message.payload);
      return;
    }
    this.options.onTelemetry?.({
      name: 'nova_error',
      data: { code: message.payload.code, message: message.payload.message },
    });
    this.options.onFatalError('nova-error');
  };

  private post(message: HostToNovaMessage): void {
    this.iframe?.contentWindow?.postMessage(message, window.location.origin);
  }

  private failHandshake(cause: string): void {
    if (this.failed || this.handshakeComplete) return;
    this.failed = true;
    this.clearTimeouts();
    this.options.onTelemetry?.({ name: 'handshake_timeout', data: { cause, loaderVersion: LOADER_VERSION } });
    this.options.onFatalError('handshake-timeout');
  }

  private clearTimeouts(): void {
    if (this.loadTimeout) clearTimeout(this.loadTimeout);
    if (this.handshakeTimeout) clearTimeout(this.handshakeTimeout);
    this.loadTimeout = undefined;
    this.handshakeTimeout = undefined;
  }
}
